/**
*
* Gallery
*
*/

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Button } from 'react-bootstrap';
import { useKey } from 'react-use';
import { useSwipeable } from 'react-swipeable';
import Icon2 from '../Icon2/index.js';
import PinchZoomPan from './PinchZoomPan/index.jsx';
import boundDimensions from './boundDimensions.js';
import Video from '../Video/index.jsx';
import Img from '../Img/index.jsx';
import { LightBox } from './LightBox.js';

const defaultSize = {
  imgWidth: 400,
  imgHeight: 400,
};

const Wrapper = styled.div`
  padding-top: 50px;
  @media (max-width: 767px) {
    padding-top: 30px;
  }
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ContentDiv = styled.div`
  width: ${(p) => p.width};
  max-width: calc(100% - 80px);
  transition: transform 0.3s ease;
  overflow: hidden; // in case of IE/Edge
  
  img, video {
    max-width: 100%;
    max-height: 90vh;
    margin: auto;
    background-color: white;
  }
  .swipe-up {
    transform: translate(0, -10px);
  }
  
  .swipe-down {
    transform: translate(0, 10px);
  }
  
  .swipe-left {
    transform: translate(-10px, 0);
  }
  
  .swipe-right {
    transform: translate(10px, 0);
  }
`;

const ButtonIcon = styled(Icon2)`
  font-size: 40px;
  color: #eee !important;
  padding: 10px;
;
;
  @media (max-width: 767px) {
    font-size: 24px;
    padding: 5px;
  }
  &[disabled] {
    cursor: default;
    color: #555;
  }
`;

function Gallery(props) {
  const { items, startIndex = 0, onClose } = props;
  const [size, setSize] = useState(defaultSize);
  const [currentIndex, setIndex] = useState(startIndex);
  const [swipeDir, setSwipeDir] = useState('');
  const maxIndex = items.length - 1;

  useEffect(() => {
    // Prevent scrolling of background
    const { scrollTop } = document.documentElement;
    const { body } = document;
    body.style.overflow = 'hidden';
    // document.getElementById('next-button').focus();
    return () => {
      body.style.overflow = 'visible';
      document.documentElement.scrollTop = scrollTop;
    };
  }, []);

  useKey('ArrowLeft', onPrevious);
  useKey('ArrowRight', onNext);
  useKey(' ', onNext);
  useKey('Escape', onClose);
  useKey('Home', () => setIndex(0));
  useKey('End', () => setIndex(maxIndex));

  function onPrevious() {
    setIndex((i) => Math.max(i - 1, 0));
  }

  function onNext() {
    setIndex((i) => Math.min(i + 1, maxIndex));
  }

  function imageLoaded(ev) {
    const img = ev.target;
    setSize({
      imgWidth: img.naturalWidth,
      imgHeight: img.naturalHeight,
    });
  }

  const handlers = useSwipeable({
    onSwiped: () => setSwipeDir(''),
    onSwipedUp: onClose,
    onSwipedDown: onClose,
    onSwipedLeft: onNext,
    onSwipedRight: onPrevious,
    onSwipingUp: () => setSwipeDir('up'),
    onSwipingDown: () => setSwipeDir('down'),
    onSwipingLeft: () => setSwipeDir('left'),
    onSwipingRight: () => setSwipeDir('right'),
    delta: 20,
    trackMouse: true,
  });

  if (startIndex === -1 || currentIndex === -1 || items.length === 0) return null;
  const { imgWidth, imgHeight } = size;
  const bounded = boundDimensions(
    imgWidth,
    imgHeight,
    window.innerWidth - 80,
    window.innerHeight - 80,
  );
  const item = items[currentIndex];

  return (
    <LightBox>
      <div className="position-absolute top-0 end-0">
        <Button variant="link" onClick={onClose}>
          <ButtonIcon className="ss-delete" />
        </Button>
      </div>
      <Wrapper>
        <Button variant="link" disabled={currentIndex === 0} onClick={onPrevious}>
          <ButtonIcon className="ss-navigateleft" />
        </Button>
        <ContentDiv width={bounded.w}>
          <PinchZoomPan width={bounded.w} height={bounded.h} key={currentIndex}>
            {(x, y, scale) => (
              <div
                {...handlers}
                className={`swipe-${swipeDir}`}
                style={{
                  width: '100%',
                  pointerEvents: scale === 1 ? 'auto' : 'none',
                  transform: `translate3d(${x}px, ${y}px, 0) scale(${scale})`,
                  transformOrigin: '0 0',
                }}
              >
                <div className="lb-media">
                  {item.type === 'image'
                    ? <Img src={item.fullPath || item.preview} alt="" onLoad={imageLoaded} />
                    : <Video video={item} />}
                </div>
              </div>
            )}
          </PinchZoomPan>
          <div className="float-end text-light">
            {`${currentIndex + 1}/${items.length}`}
          </div>
        </ContentDiv>
        <Button autoFocus variant="link" disabled={currentIndex === maxIndex} onClick={onNext}>
          <ButtonIcon className="ss-navigateright" />
        </Button>
      </Wrapper>
    </LightBox>
  );
}

Gallery.propTypes = {
  items: PropTypes.array.isRequired,
  startIndex: PropTypes.number,
  onClose: PropTypes.func,
};

export default Gallery;
